import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useVehicleDailyAudits, useVehicleDailyAudit, useReopenVehicleDailyAudit } from "hooks/useVehicleDailyAudit"
import { formatReadableDate } from "utils/formatDate"
import { downloadAndOpenFile } from "api/pdfApi"
import StockModelCell from "components/Common/stock-table/StockModelCell"
import MovementInfoCell from "components/Common/VehicleMovement/MovementInfoCell"

const nameField = value => (value ? { name: value } : null)

// Items are printed from the frozen vehicle_snapshot/movement_snapshot (flat
// strings), never live vehicle data — a reprint must always match what was
// true at close time even if the vehicle's spec changed since. Both cell
// components expect nested `{ name }` relation objects, so adapt the flat
// snapshot into that shape here rather than changing the frozen format.
const snapshotToStockShape = item => {
  const v = item.vehicle_snapshot ?? {}
  return {
    stock_id: v.stock_id,
    full_chassis: v.full_chassis,
    brand: nameField(v.brand),
    model: nameField(v.model),
    package: nameField(v.package),
    year: nameField(v.year),
    engine_c_c: nameField(v.engine_cc),
    engine_type: nameField(v.engine_type),
    fuel_type: nameField(v.fuel_type),
    transmission: nameField(v.transmission),
    key_type: nameField(v.key_type),
    exterior_color: nameField(v.exterior_color),
    interior_color: nameField(v.interior_color),
    auction_grade: nameField(v.auction_grade),
    mileage: item.mileage,
  }
}

const snapshotToMovementShape = item => {
  const m = item.movement_snapshot
  if (!m) return null

  return {
    movement_type: nameField(m.movement_type),
    location_from: nameField(m.location_from),
    location_to: nameField(m.location_to),
    movement_date: m.movement_date,
    movement_by: nameField(m.moved_by),
    mileage: m.mileage,
  }
}

const HISTORY_SECTIONS = [
  { key: "present", title: "Present", badge: "success" },
  { key: "moved_out", title: "Moved Out", badge: "primary" },
  { key: "missing", title: "Missing", badge: "danger" },
]

const DailyVehicleAuditHistory = () => {
  const [page, setPage] = useState(1)
  const [reopenTarget, setReopenTarget] = useState(null)
  const [reopenReason, setReopenReason] = useState("")
  const [viewTargetId, setViewTargetId] = useState(null)
  const [exportingId, setExportingId] = useState(null)

  const queryParams = useMemo(
    () => ({ page, per_page: 10 }),
    [page]
  )

  const { data: responseData, isLoading, refetch, isRefetching } = useVehicleDailyAudits(queryParams)
  const { mutateAsync: reopenMutation, isPending: reopenLoading } = useReopenVehicleDailyAudit()
  const { data: viewResponse, isLoading: viewLoading } = useVehicleDailyAudit(viewTargetId, !!viewTargetId)
  const viewAudit = viewResponse?.data

  const handleExportPdf = audit =>
    downloadAndOpenFile({
      url: `/vehicle-daily-audits/${audit.id}/pdf`,
      openInNewTab: false,
      filename: `vehicle-daily-audit-${audit.audit_date}.pdf`,
      onStart: () => setExportingId(audit.id),
      onEnd: () => setExportingId(null),
    })

  const toggleReopenModal = () => setReopenTarget(null)

  const handleReopenClick = item => {
    setReopenReason("")
    setReopenTarget(item)
  }

  const handleReopenSubmit = async () => {
    if (!reopenReason.trim()) return
    try {
      const res = await reopenMutation({ id: reopenTarget.id, reason: reopenReason.trim() })
      toast.success(res?.message ?? "Audit reopened successfully.")
      setReopenTarget(null)
      refetch()
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to reopen the audit."
      toast.error(msg)
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Audit Date",
        cell: ({ row }) => formatReadableDate(row.original.audit_date),
      },
      {
        header: "Closed By",
        cell: ({ row }) => row.original.closed_by?.name || row.original.closedBy?.name || "-",
      },
      {
        header: "Total Vehicles",
        cell: ({ row }) => row.original.total_vehicles,
      },
      {
        header: "Moved Out",
        cell: ({ row }) => row.original.moved_out_count,
      },
      {
        header: "New Arrivals",
        cell: ({ row }) => row.original.new_arrival_count,
      },
      {
        header: "Status",
        cell: ({ row }) =>
          row.original.reopened_at ? (
            <Badge color="secondary">Reopened</Badge>
          ) : (
            <Badge color="dark">Closed</Badge>
          ),
      },
      {
        header: "Reopened",
        cell: ({ row }) =>
          row.original.reopened_at ? (
            <div className="small">
              <div>{row.original.reopened_by?.name || row.original.reopenedBy?.name || "-"}</div>
              <div className="text-muted">{formatReadableDate(row.original.reopened_at)}</div>
              <div className="text-muted fst-italic">{row.original.reopen_reason}</div>
            </div>
          ) : (
            "-"
          ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button
              color="light"
              size="sm"
              onClick={() => setViewTargetId(row.original.id)}
              id={`view-vehicle-daily-audit-${row.original.id}`}
            >
              <i className="mdi mdi-eye-outline" />
            </Button>
            <UncontrolledTooltip placement="top" target={`view-vehicle-daily-audit-${row.original.id}`}>
              View
            </UncontrolledTooltip>

            <Button
              color="light"
              size="sm"
              onClick={() => handleExportPdf(row.original)}
              disabled={exportingId === row.original.id}
              id={`pdf-vehicle-daily-audit-${row.original.id}`}
            >
              <i className={`mdi mdi-file-pdf-box ${exportingId === row.original.id ? "mdi-spin" : ""}`} />
            </Button>
            <UncontrolledTooltip placement="top" target={`pdf-vehicle-daily-audit-${row.original.id}`}>
              PDF
            </UncontrolledTooltip>

            <CanView access={[ACCESS.VEHICLE_MOVEMENT.REOPEN_DAILY_AUDIT]}>
              {!row.original.reopened_at && (
                <>
                  <Button
                    color="warning"
                    size="sm"
                    onClick={() => handleReopenClick(row.original)}
                    id={`reopen-vehicle-daily-audit-${row.original.id}`}
                  >
                    <i className="mdi mdi-lock-open-variant-outline" />
                  </Button>
                  <UncontrolledTooltip placement="top" target={`reopen-vehicle-daily-audit-${row.original.id}`}>
                    Reopen
                  </UncontrolledTooltip>
                </>
              )}
            </CanView>
          </div>
        ),
      },
    ],
    [exportingId]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <Card className="border">
      <CardBody>
        <div className="d-flex align-items-center mb-3">
          <h5 className="mb-0 flex-grow-1">Audit History</h5>
          <Button color="light" size="sm" onClick={refetch}>
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
        </div>

        <TableContainer
          columns={columns}
          data={data}
          isPagination={true}
          paginationInfo={paginationInfo}
          onPageChange={setPage}
          loading={isLoading || isRefetching}
        />

        <Modal isOpen={!!reopenTarget} toggle={toggleReopenModal}>
          <ModalHeader toggle={toggleReopenModal}>
            Reopen {reopenTarget ? formatReadableDate(reopenTarget.audit_date) : ""}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={12}>
                <Label htmlFor="reopen_reason">Reason</Label>
                <Input
                  type="textarea"
                  id="reopen_reason"
                  value={reopenReason}
                  onChange={e => setReopenReason(e.target.value)}
                  invalid={!reopenReason.trim() && reopenReason !== ""}
                />
                <FormFeedback>Please provide a reason for reopening this audit.</FormFeedback>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={toggleReopenModal}>Cancel</Button>
            <Button color="warning" onClick={handleReopenSubmit} disabled={reopenLoading || !reopenReason.trim()}>
              {reopenLoading ? "Reopening..." : "Reopen"}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={!!viewTargetId} toggle={() => setViewTargetId(null)} size="lg">
          <ModalHeader toggle={() => setViewTargetId(null)}>
            {viewAudit ? `Closed ${formatReadableDate(viewAudit.audit_date)}` : "Audit"}
          </ModalHeader>
          <ModalBody>
            {viewLoading ? (
              <div className="text-center py-4 text-muted">Loading...</div>
            ) : !viewAudit ? (
              <div className="text-center py-4 text-muted">No data available.</div>
            ) : (viewAudit.items ?? []).length === 0 ? (
              <div className="text-center py-4 text-muted">No vehicles recorded for this close.</div>
            ) : (
              HISTORY_SECTIONS.map(section => {
                const items = viewAudit.items.filter(item => item.status === section.key)

                return (
                  <div key={section.key} className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <strong>{section.title}</strong>
                      <Badge color={section.badge}>{items.length}</Badge>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: 220, overflowY: "auto" }}>
                      <Table bordered size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Vehicle</th>
                            <th>Last Movement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr><td colSpan={2} className="text-center text-muted">None.</td></tr>
                          ) : (
                            items.map(item => (
                              <tr key={item.id}>
                                <td>
                                  <StockModelCell
                                    stock={snapshotToStockShape(item)}
                                    isExpanded={() => false}
                                    toggleRow={() => {}}
                                    showIdentifiers
                                  />
                                </td>
                                <td><MovementInfoCell data={snapshotToMovementShape(item)} /></td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )
              })
            )}
          </ModalBody>
          <ModalFooter>
            {viewAudit && (
              <Button
                color="success"
                outline
                onClick={() => handleExportPdf(viewAudit)}
                disabled={exportingId === viewAudit.id}
              >
                <i className={`mdi mdi-file-pdf-box me-1 ${exportingId === viewAudit.id ? "mdi-spin" : ""}`} />
                Export PDF
              </Button>
            )}
            <Button color="light" onClick={() => setViewTargetId(null)}>Close</Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  )
}

export default DailyVehicleAuditHistory
